import { PropGetters } from "downshift";
import sum from "lodash-es/sum";
import sumBy from "lodash-es/sumBy";
import React, { useLayoutEffect, useRef } from "react";
import { VariableSizeList } from "react-window";
import styled from "styled-components";
import { rootFontSize } from "../../cssVariables";
import { extractFromStringLocationId } from "../../graphQL/Utils";
import usePrevious from "../../tree/usePreviousHook";
import { secondaryColor2 } from "../Utils";
import { menuMaxHeight, offsetValue } from "./ConclusionPageCountrySelector";
import { OptionWithHeight, VisualProps } from "./Dropdown";

//#region Styling
const Root = styled.div`
  height: 0;
`;
export const flagWidth = 25; // in pixels
export const flagToTextSeparation = 10; // in pixels
export const menuItemLineHeight = 1.5; // scalar
export const getMenuItemLeftPadding = (visualProps: VisualProps) =>
  visualProps.magnifyingGlassWidth + visualProps.magnifyingGlassGap;
const MenuItemContainer = styled.div<{ visualProps: VisualProps }>`
  padding: 0 0 0 ${({ visualProps }) => getMenuItemLeftPadding(visualProps)}rem;
  display: grid;
  grid-template-columns: ${flagWidth}px 1fr;
  grid-column-gap: ${flagToTextSeparation}px;
  cursor: pointer;
  background-color: white;
`;
const FlagContainer = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const countryNameFontSize = 16; // in pixels

const CountryName = styled.div`
  grid-column: 2;
  grid-row: 1;
  font-size: ${countryNameFontSize}px;
  font-weight: 400;
  font-style: normal;
  line-height: ${menuItemLineHeight};
  display: flex;
  align-items: center;
`;
//#endregion

interface ItemRendererData {
  highlightedIndex: number | null;
  items: OptionWithHeight[];
  getItemProps: PropGetters<OptionWithHeight>["getItemProps"];
  visualProps: VisualProps;
  selectedItemValue: string;
}
interface ItemRendererProps {
  index: number;
  style: React.CSSProperties;
  data: ItemRendererData;
}
const ItemRenderer = React.memo((props: ItemRendererProps) => {
  const {
    index,
    style: reactWindowStyle,
    data: {
      highlightedIndex,
      items,
      getItemProps,
      visualProps,
      selectedItemValue,
    },
  } = props;

  const item = items[index];
  const itemProps = getItemProps({ item, index });
  const isHighlighted =
    (highlightedIndex !== null && index === highlightedIndex) ||
    item.value === selectedItemValue;
  const style = {
    ...reactWindowStyle,
    backgroundColor: isHighlighted ? secondaryColor2 : "",
    opacity: 1,
  };
  const numericId = extractFromStringLocationId(item.value);
  const src = require("../../img/svgs-loaded-with-file-loader/country_flags/Flag-" +
    numericId +
    ".svg");
  return (
    <MenuItemContainer visualProps={visualProps} {...itemProps} style={style}>
      <FlagContainer>
        <img src={src} />
      </FlagContainer>
      <CountryName>{item.label}</CountryName>
    </MenuItemContainer>
  );
});

const haveItemsChanged = (
  prevItems: OptionWithHeight[] | undefined,
  nextItems: OptionWithHeight[]
) => {
  if (prevItems === undefined) {
    return true;
  } else {
    const prevLength = prevItems.length;
    const nextLength = nextItems.length;
    if (prevLength !== nextLength) {
      return true;
    } else {
      for (let i = 0; i < prevLength; i += 1) {
        const prevItem = prevItems[i];
        const nextItem = nextItems[i];
        if (prevItem.value !== nextItem.value) {
          return true;
        }
      }
      return false;
    }
  }
};

interface Props {
  menuProps: object;
  getItemProps: PropGetters<OptionWithHeight>["getItemProps"];
  items: OptionWithHeight[];
  highlightedIndex: number | null;
  visualProps: VisualProps;
  selectedItemValue: string;
  useTriggerButton: boolean;
}

const Menu = (props: Props) => {
  const {
    menuProps,
    getItemProps,
    items,
    highlightedIndex,
    selectedItemValue,
    visualProps,
    useTriggerButton,
  } = props;

  const totalHeight = sum(items.map((item) => item.height));
  const reactWindowHeight =
    totalHeight < visualProps.menuMaxHeight
      ? totalHeight
      : visualProps.menuMaxHeight;
  let initialScrollOffset: number;
  if (highlightedIndex === null) {
    initialScrollOffset = 0;
  } else {
    initialScrollOffset = 0;
    for (const { height, value } of items) {
      if (value === selectedItemValue) {
        break;
      }
      initialScrollOffset += height;
    }
  }

  const listInstanceRef = useRef<VariableSizeList | null>(null);
  const prevItems = usePrevious(items);
  useLayoutEffect(() => {
    const nextItems = items;
    const { current: listInstance } = listInstanceRef;
    if (
      haveItemsChanged(prevItems, nextItems) === true &&
      listInstance !== null
    ) {
      listInstance.resetAfterIndex(0, true);
    }
  });
  useLayoutEffect(
    () => {
      const { current: listInstance } = listInstanceRef;
      if (listInstance !== null && highlightedIndex !== null) {
        listInstance.scrollToItem(highlightedIndex);
      }
    },
    [highlightedIndex]
  );

  const averageHeight = sumBy(items, (item) => item.height) / items.length;
  const getItemSize = (index: number) => items[index].height;
  const itemData: ItemRendererData = {
    highlightedIndex,
    items,
    getItemProps,
    visualProps,
    selectedItemValue,
  };
  const itemKey = (index: number, data: ItemRendererData) =>
    data.items[index].value;
  const menuHeight =
    useTriggerButton === false ? reactWindowHeight : menuMaxHeight;
  const style: React.CSSProperties =
    useTriggerButton === false
      ? { backgroundColor: "white" }
      : {
          backgroundColor: "white",
          transform: `translateY(${offsetValue}px)`,
        };
  return (
    <Root>
      <VariableSizeList
        {...menuProps}
        children={ItemRenderer}
        itemData={itemData}
        height={menuHeight}
        initialScrollOffset={initialScrollOffset}
        itemCount={items.length}
        estimatedItemSize={averageHeight}
        itemSize={getItemSize}
        width={visualProps.totalWidth * rootFontSize}
        style={style}
        itemKey={itemKey}
        ref={listInstanceRef}
      />
    </Root>
  );
};

export default Menu;
